import { Accordion, Typography } from '@navi/web-ui/lib/primitives';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import { DateFormat, UnitsEnum, dateDiff, dateFormat, utcToIST } from 'src/utils/DateHelper';
import GridContainer from '@navi/web-ui/lib/layouts/Grid/GridContainer/GridContainer';
import GridRow from '@navi/web-ui/lib/layouts/Grid/GridRow/GridRow';
import GridColumn from '@navi/web-ui/lib/layouts/Grid/GridColumn/GridColumn';
import { CallBasedInteractions, PtpStatus } from '../../interfaces/CaseDetail.type';
import { PreviousPTPs } from './PreviousPTPs';
import CallInteractionHistory from 'src/components/CallHistory/CallInteractionHistory';
import { IconProps } from '@navi/web-ui/lib/icons/types';
import CircularProgress from '@cp/components/ProgressBars/circularProgress/CircularProgress';
import Card from '@navi/web-ui/lib/primitives/Card/Card';
import styles from './index.module.scss';
import cx from 'classnames';
import dayjs from 'dayjs';
import { LIMIT_CALLS } from '@cp/src/constants/Common.constants';

const NO_OF_RECORDS = 5;

const getPreviousDate = () => {
  const date = new Date();
  date.setDate(new Date().getDate() - 1);
  return date;
};

const CallHistoryBox = () => {
  const { loanId = '', customerId = '' } = useParams();
  const pageData = useSelector(
    (state: RootState) => state?.caseDetail?.pageData?.[createKey(loanId, customerId)]
  );
  const { data: telephones } = pageData?.telephonesv2 || {};
  const { data: caseDetails } = pageData?.details || {};
  const { callBasedInteractions = [], ptpStatuses = [] } = caseDetails || {};

  const lastCalledTime = [...callBasedInteractions]
    .sort((a: CallBasedInteractions, b: CallBasedInteractions) => {
      return new Date(a?.interactionTimestamp) > new Date(b?.interactionTimestamp) ? 1 : -1;
    })
    .pop()?.interactionTimestamp;
  const currentDate = dateFormat(new Date(), DateFormat.ISO_DATE_FORMAT);
  const previousDayDate = dateFormat(getPreviousDate(), DateFormat.ISO_DATE_FORMAT);
  const difference = lastCalledTime
    ? Math.abs(
        dateDiff(new Date(), new Date(utcToIST(lastCalledTime as unknown as Date)), UnitsEnum.Days)
      )
    : undefined;
  const iconProps: IconProps = { width: 24, height: 24 };
  const LastCalled = () => {
    const dateInIST = new Date(utcToIST(lastCalledTime as unknown as Date));
    const lastCalledDate = dateFormat(dateInIST, DateFormat.ISO_DATE_FORMAT);
    const lastCalledDateLong = dateFormat(dateInIST, DateFormat.LONG_DATE_FORMAT_WITHOUT_TIME);
    const lastCalledTimeHHMM = dateFormat(dateInIST, DateFormat.HH_mm_ampm);
    if (lastCalledTime) {
      switch (lastCalledDate) {
        case currentDate:
          return (
            <Typography as="span" variant="h3">
              Today at {lastCalledTimeHHMM}
            </Typography>
          );
        case previousDayDate:
          return (
            <Typography as="span" variant="h3">
              Yesterday at {lastCalledTimeHHMM}
            </Typography>
          );
        default:
          return (
            <>
              <Typography as="span" variant="h3">
                Not called since last {difference} days{' '}
              </Typography>
              <Typography as="span" variant="h5" color="var(--grayscale-content-3)">
                (Last call attempt: {lastCalledDateLong})
              </Typography>
            </>
          );
      }
    } else {
      return (
        <Typography as="span" variant="h3">
          NA
        </Typography>
      );
    }
  };

  const { totalCallsRemaining, nextCallTime, callRemainingPercentage } = useMemo(() => {
    let newTotalCallsRemaining = 0;
    let totalMaxCalls = 0;
    let newLastCallTime: dayjs.Dayjs | null = null;

    for (const telephone of telephones || []) {
      const dailyLimit = telephone?.limit?.DAILY;
      newTotalCallsRemaining += dailyLimit?.callRemaining;
      totalMaxCalls += dailyLimit?.maxCalls;
    }

    for (const telephone of telephones || []) {
      const dailyLimit = telephone?.limit?.DAILY;
      const hourlyLimit = telephone?.limit?.HOURLY;

      if (dailyLimit?.callRemaining > 0 && hourlyLimit?.nextCallingTime) {
        const nextCallAvailableTime = dayjs(hourlyLimit?.nextCallingTime);

        if (!newLastCallTime || nextCallAvailableTime.isBefore(newLastCallTime)) {
          newLastCallTime = nextCallAvailableTime;
        }
      }
    }

    const nextCallTime = newLastCallTime ? newLastCallTime.format('h:mm A') : null;

    const callRemainingPercentage = Math.round((newTotalCallsRemaining / totalMaxCalls) * 100);

    return {
      totalCallsRemaining: newTotalCallsRemaining,
      nextCallTime,
      callRemainingPercentage:
        callRemainingPercentage > 90 && callRemainingPercentage < 100 ? 90 : callRemainingPercentage
    };
  }, [telephones]);

  return (
    <GridContainer className={styles.callHistoryWrapper}>
      <GridRow>
        <GridColumn
          md={12}
          style={{
            padding: '0 8px'
          }}
        >
          <Card className={cx(styles.cardWrapper, 'mb-[24px]')}>
            <div className={styles.cardHeader}>
              <Typography as="div" variant="h3" className={styles.heading}>
                Last Called : <LastCalled />
              </Typography>
              <div>
                <CallInteractionHistory
                  callBasedInteractions={callBasedInteractions}
                  noOfRecords={NO_OF_RECORDS}
                  showTooltip
                />
              </div>
            </div>
            <div className={styles.cardHeader}>
              <div className="flex gap-[8px] items-center">
                <CircularProgress
                  sqSize={20}
                  percentage={callRemainingPercentage}
                  strokeWidth={3}
                  withAnimation
                />
                <Typography variant="h5" color="var(--grayscale-2)">
                  {totalCallsRemaining} calls remaining
                </Typography>
                {nextCallTime ? (
                  <>
                    <Typography variant="h5" color="var(--grayscale-3)">
                      |
                    </Typography>

                    {totalCallsRemaining === 0 ? (
                      <>
                        <Typography variant="h5" color="var(--grayscale-2)">
                          {LIMIT_CALLS.DAILY_LIMIT_REACHED}
                          <Typography
                            as="span"
                            variant="h5"
                            className="font-medium"
                            color="var(--orange-darkbase)"
                          >
                            {LIMIT_CALLS.TOMORROW}
                          </Typography>
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="h5" color="var(--grayscale-2)">
                          {LIMIT_CALLS.HOURLY_LIMIT_REACHED}
                          {dayjs(nextCallTime).isAfter(dayjs(), 'day') ? (
                            <Typography
                              as="span"
                              variant="h5"
                              className="font-medium"
                              color="var(--orange-darkbase)"
                            >
                              {LIMIT_CALLS.TOMORROW}
                            </Typography>
                          ) : (
                            <Typography
                              as="span"
                              variant="h5"
                              className="font-medium"
                              color="var(--orange-darkbase)"
                            >
                              {`${nextCallTime}`}
                            </Typography>
                          )}
                        </Typography>
                      </>
                    )}
                  </>
                ) : null}
              </div>
              <div>
                <Typography as="div" variant="h4" className={cx(styles.subtext, styles.pv0)}>
                  Last {NO_OF_RECORDS} calls Status
                </Typography>
              </div>
            </div>
          </Card>
        </GridColumn>
      </GridRow>
    </GridContainer>
  );
};

export default CallHistoryBox;
